import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCurrentUser, logout as logoutApi } from '../services/auth';
import { listChatSessions } from '../services/api';
import { getProjects } from '../services/api';

interface User {
  email: string;
  name: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [token, setToken] = useState<string | null>(() =>
    localStorage.getItem('authToken')
  );
  const queryClient = useQueryClient();

  const { data: user, isLoading } = useQuery({
    queryKey: ['currentUser'],
    queryFn: getCurrentUser,
    enabled: !!token,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const logoutMutation = useMutation({
    mutationFn: logoutApi,
    onSettled: () => {
      localStorage.removeItem('authToken');
      setToken(null);
      queryClient.clear();
      window.location.href = '/login';
    },
  });

  const login = (newToken: string) => {
    queryClient.clear();
    localStorage.setItem('authToken', newToken);
    setToken(newToken);
    // Prefetch critical data for chat UI
    queryClient.prefetchQuery({
      queryKey: ['projects'],
      queryFn: getProjects,
      staleTime: 10 * 60 * 1000,
    });
    queryClient.prefetchQuery({
      queryKey: ['chat-sessions'],
      queryFn: listChatSessions,
      staleTime: 2 * 60 * 1000,
    });
  };

  const logout = () => {
    logoutMutation.mutate();
  };

  // Check if token is expired or invalid
  useEffect(() => {
    if (token && !isLoading && !user) {
      // Token is invalid, clear it
      localStorage.removeItem('authToken');
      setToken(null);
    }
  }, [token, isLoading, user]);

  const value: AuthContextType = {
    user: user || null,
    isLoading: isLoading && !!token,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
